// frontend/src/components/staff/BroadcastComposePage.js
//
// MAILCHIMP-COMMS-001-B — Operational Broadcast Compose Surface (FE).
// Route: /staff/communications/broadcast  (mounted in App.js).
//
// Story:    docs/backlog/MAILCHIMP-COMMS-001-B-fe-compose-surface.md
// Tech doc: docs/architecture/mailchimp-comms-001-A-tech.md
// Visual:   docs/design/MAILCHIMP-COMMS-001-A-design.md
//
// Scope (Slice 1A):
//   - RBAC admin + staff (mt allowed per Roderick lock; BE final gate)
//   - Recipient picker drawer (multi-select from /staff/customers/search)
//   - Step-driven compose layout: Recipients -> Subject -> Body
//   - Body tabs: Template (deferred — BE templates endpoint NOT yet shipped
//     in PR #156; gracefully degrades to a disabled tab with explanation)
//     and Freeform (rich-text via existing react-quill)
//   - Send-confirm modal with soft warning
//   - Surfaces RECIPIENT_CAP_EXCEEDED and BODY_SANITIZED_TO_EMPTY per spec
//   - ADMIN MODE bar role-conditional
//
// Production-safety: pure-additive new route + new component cluster. Zero
// edits to existing pages, contexts, or services beyond the new
// broadcastAPI entry. Single-revert: drop the route in App.js + delete
// these files = clean rollback.
//
// State management: codebase has NO TanStack Query (verified via
// package.json — react-toastify + plain axios + useState/useEffect is the
// convention, mirroring AdminStaffAvailabilityDashboard's approach). The
// tech doc's TanStack key references are honored semantically — fetches
// are re-fired on filter / page state change.
//
// BE contract reminders (from commsController.js in PR #156):
//   POST /api/comms/broadcast returns 202 with:
//     { broadcastLogId, status, recipientCount, message }
//   The fanout runs server-side post-202 (fire-and-forget). The FE polls
//   GET /api/comms/broadcast/:id to observe status flips (this story
//   surfaces the 202 + a success toast; the poll surface is the sister
//   MAILCHIMP-COMMS-001-C broadcast history page).
//
// Story file references `audienceTag: "all_active_customers"` and a
// templates fetch endpoint — those endpoints are NOT in the merged BE
// contract (PR #156). The Slice 1A BE accepts `recipientUserIds` +
// `recipientEmails`; recipient selection is driven by the picker drawer
// per the visual spec (which IS aligned with the BE reality). The Template
// tab renders an empty-state explanation until the templates endpoint
// ships in a follow-up — Send is fully functional via the Freeform tab.

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPaperPlane,
  FaTimes,
  FaUserPlus,
  FaExclamationTriangle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../AuthContext";
import { broadcastAPI } from "../../services/api";
import RecipientPickerDrawer from "./RecipientPickerDrawer";
import SendConfirmModal from "./SendConfirmModal";
import FreeformBodyEditor from "./FreeformBodyEditor";
import "./BroadcastComposePage.css";

// Hard caps from BE (commsController.js DOMPURIFY config + SUBJECT_MAX_LENGTH
// + BODY_MAX_BYTES). The FE mirrors them to avoid round-tripping a 400 for
// a trivially-too-long input — the BE remains the hard gate.
const SUBJECT_MAX_LENGTH = 200;
const SUBJECT_SOFT_WARN = 110;
const SUBJECT_HARD_BLOCK = 121;
const BODY_SOFT_WARN_CHARS = 3500;
const BODY_HARD_BLOCK_CHARS = 4001;
const BODY_MAX_BYTES = 100 * 1024; // BE byte-length cap

// Cap badges per role (BE-locked: staff 50 / admin 500; env-overridable
// server-side but the FE renders the default — actual gate is server-side).
const STAFF_RECIPIENT_CAP = 50;
const ADMIN_RECIPIENT_CAP = 500;

// Map BE error codes to human-readable messages per Zuri spec §5 + §3.4.
const ERROR_COPY = {
  RECIPIENT_CAP_EXCEEDED: ({ cap, role, attempted }) =>
    `Send limit reached: ${role} can send to up to ${cap} customers per broadcast. You selected ${attempted}. Remove recipients or contact an admin.`,
  BODY_SANITIZED_TO_EMPTY:
    "Your message body was stripped during safety sanitization. Remove disallowed tags (scripts, styles) and try again.",
  MISSING_FREEFORM_CONTENT:
    "Both subject and message body are required to send a freeform broadcast.",
  NO_RECIPIENTS: "Select at least one customer to send to.",
  SUBJECT_TOO_LONG: `Subject must be at most ${SUBJECT_MAX_LENGTH} characters.`,
  BODY_TOO_LARGE:
    "Message body is too large. Trim the content and try again (limit ~100KB).",
  INVALID_COMPOSE_MODE:
    "Compose mode is invalid. Refresh the page and try again.",
  MISSING_TEMPLATE: "Pick a valid template before sending.",
  ACCESS_DENIED: "You do not have permission to send broadcasts.",
};

const BroadcastComposePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const recipientCap = isAdmin ? ADMIN_RECIPIENT_CAP : STAFF_RECIPIENT_CAP;

  // Compose-mode tabs. Template mode is rendered but disabled when the
  // BE templates endpoint is not yet shipped (Slice 1A PR #156). Freeform
  // is the active default-and-only-working surface in Slice 1A.
  const [composeMode, setComposeMode] = React.useState("freeform");

  // Recipients picker state. The picker maintains its own staging selection
  // internally; on commit it pushes the full array up here.
  const [recipients, setRecipients] = React.useState([]); // [{ userId, email, displayName, isMember }]
  const [pickerOpen, setPickerOpen] = React.useState(false);

  // Compose state.
  const [subject, setSubject] = React.useState("");
  const [bodyHtml, setBodyHtml] = React.useState("");

  // The plaintext char-count derived from bodyHtml — used for the visible
  // counter + soft/hard caps that the BE enforces in bytes. We strip HTML
  // tags for the counter so users see the literal-text length that the
  // recipient will read, not the marked-up HTML length.
  const bodyCharCount = React.useMemo(() => {
    if (!bodyHtml) return 0;
    return bodyHtml.replace(/<[^>]+>/g, "").trim().length;
  }, [bodyHtml]);

  // Confirm modal + in-flight send state.
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState(null); // { code, message, details? }

  // ----- Validation gates -------------------------------------------------

  // Subject validation
  const subjectInvalid =
    subject.length === 0 || subject.length >= SUBJECT_HARD_BLOCK;

  // Body validation
  const bodyInvalid =
    composeMode === "freeform" &&
    (bodyCharCount === 0 || bodyCharCount >= BODY_HARD_BLOCK_CHARS);

  // Recipient gates
  const recipientCountInvalid = recipients.length === 0;
  const recipientOverCap = recipients.length > recipientCap;

  // Template mode is not shipped in Slice 1A — disable Send when active.
  const templateModeBlocked = composeMode === "template";

  const sendDisabled =
    sending ||
    subjectInvalid ||
    bodyInvalid ||
    recipientCountInvalid ||
    recipientOverCap ||
    templateModeBlocked;

  // Compose disabled-state hint for the Send tooltip / a11y.
  const sendDisabledReason = (() => {
    if (recipientCountInvalid) return "Select at least one recipient to send.";
    if (recipientOverCap)
      return `Selected ${recipients.length} exceeds your ${recipientCap}-recipient limit.`;
    if (subjectInvalid) return "Fill in a valid subject to send.";
    if (bodyInvalid) return "Fill in the message body to send.";
    if (templateModeBlocked)
      return "Template broadcasts will be available in an upcoming release.";
    return "";
  })();

  // Char counter color stage (per Zuri spec §3.5 + §3.6.3).
  const subjectCounterStage =
    subject.length >= SUBJECT_HARD_BLOCK
      ? "block"
      : subject.length >= SUBJECT_SOFT_WARN
      ? "warn"
      : "ok";

  const bodyCounterStage =
    bodyCharCount >= BODY_HARD_BLOCK_CHARS
      ? "block"
      : bodyCharCount >= BODY_SOFT_WARN_CHARS
      ? "warn"
      : "ok";

  // ----- Picker commit handler -------------------------------------------

  const handleRecipientsCommit = (next) => {
    // Defense-in-depth — dedupe by userId/email at commit time.
    const seen = new Set();
    const deduped = [];
    for (const r of next) {
      const key = r.userId || r.email?.toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      deduped.push(r);
    }
    setRecipients(deduped);
    setPickerOpen(false);
  };

  const handleRemoveRecipient = (recipient) => {
    setRecipients((prev) =>
      prev.filter((r) => {
        if (recipient.userId && r.userId === recipient.userId) return false;
        if (recipient.email && r.email === recipient.email) return false;
        return true;
      })
    );
  };

  // ----- Send flow --------------------------------------------------------

  const handleSendClick = () => {
    if (sendDisabled) return;
    setSendError(null);
    setConfirmOpen(true);
  };

  const handleConfirmCancel = () => {
    if (sending) return; // can't cancel mid-flight
    setConfirmOpen(false);
  };

  const handleConfirmSend = async () => {
    if (sending || sendDisabled) return;
    setSending(true);
    setSendError(null);

    const payload = {
      composeMode: "freeform",
      subject: subject.trim(),
      bodyHtml,
      recipientUserIds: recipients
        .filter((r) => r.userId)
        .map((r) => r.userId),
      recipientEmails: recipients
        .filter((r) => !r.userId && r.email)
        .map((r) => r.email),
    };

    try {
      const response = await broadcastAPI.send(payload);
      const { recipientCount, broadcastLogId } = response.data || {};
      // Success — clear the form, close the modal, surface toast, route to
      // history (sister story consumes broadcastLogId for highlighting).
      toast.success(
        `Broadcast queued for ${recipientCount || recipients.length} customer${
          (recipientCount || recipients.length) === 1 ? "" : "s"
        } — view in Send History.`,
        { autoClose: 4000 }
      );
      setConfirmOpen(false);
      setRecipients([]);
      setSubject("");
      setBodyHtml("");
      setSending(false);
      // MAILCHIMP-COMMS-001-C — navigate to the history view with a ?fresh=
      // hand-off so the newly-sent row pulses with the 2-second gold flash
      // (Zuri spec §3.7). If the BE 202 omits broadcastLogId for any
      // reason, fall back to a plain navigate so the user still lands on
      // the audit surface.
      if (broadcastLogId) {
        navigate(
          `/staff/communications/history?fresh=${encodeURIComponent(
            broadcastLogId
          )}`
        );
      } else {
        navigate("/staff/communications/history");
      }
    } catch (err) {
      setSending(false);
      const resp = err?.response?.data;
      const code = resp?.code || resp?.error || "INTERNAL_ERROR";
      const details = resp?.details || {};
      let message = resp?.message || err?.userMessage || "";

      // Map BE codes to spec'd copy when we have a mapping.
      const mapped = ERROR_COPY[code];
      if (typeof mapped === "function") {
        message = mapped({
          cap: details.cap ?? recipientCap,
          role: details.role ?? (isAdmin ? "admin" : "staff"),
          attempted: details.attempted ?? recipients.length,
        });
      } else if (typeof mapped === "string") {
        message = mapped;
      } else if (!message) {
        message = "Broadcast send failed. Try again.";
      }

      setSendError({ code, message, details });
      // Keep the modal open so the user sees the inline banner and can
      // retry without losing context.
    }
  };

  // ----- Cancel / discard -------------------------------------------------

  const handleCancel = () => {
    const dirty =
      recipients.length > 0 || subject.length > 0 || bodyHtml.length > 0;
    if (
      dirty &&
      // Confirm via window.confirm to match the codebase's lightweight
      // confirm pattern (no custom modal needed for a discard prompt).
      !window.confirm("Discard this draft? Any in-progress content will be lost.")
    ) {
      return;
    }
    navigate("/staff");
  };

  // ----- Render -----------------------------------------------------------

  return (
    <div className="broadcast-compose-page">
      {isAdmin && (
        <div
          className="broadcast-admin-mode-bar"
          role="status"
          aria-label="Admin mode"
        />
      )}

      <header className="broadcast-page-header">
        <h1>Send Communication</h1>
        <p className="broadcast-page-subtitle">
          Send a message to your customers via email (MST)
        </p>
      </header>

      <div className="broadcast-compose-body">
        {/* Step 1 — Recipients */}
        <section
          className="broadcast-step"
          aria-labelledby="broadcast-step-recipients"
        >
          <h2 id="broadcast-step-recipients" className="broadcast-step-label">
            Step 1: Recipients
          </h2>

          {recipients.length === 0 ? (
            <div className="broadcast-recipients-empty">
              <button
                type="button"
                className="broadcast-btn-outline"
                onClick={() => setPickerOpen(true)}
                aria-label="Add recipients"
              >
                <FaUserPlus aria-hidden="true" /> Add recipients
              </button>
              <span className="broadcast-recipients-hint">
                Select customers to email
              </span>
            </div>
          ) : (
            <div className="broadcast-recipients-chips">
              {recipients.map((r) => {
                const key = r.userId || r.email;
                const initialLast = r.displayName
                  ? formatFirstInitial(r.displayName)
                  : r.email;
                return (
                  <span
                    key={key}
                    className="broadcast-recipient-chip"
                    title={r.displayName || r.email}
                  >
                    <span className="broadcast-recipient-chip-name">
                      {initialLast}
                    </span>
                    <button
                      type="button"
                      className="broadcast-recipient-chip-remove"
                      aria-label={`Remove ${r.displayName || r.email}`}
                      onClick={() => handleRemoveRecipient(r)}
                    >
                      <FaTimes aria-hidden="true" />
                    </button>
                  </span>
                );
              })}
              <button
                type="button"
                className="broadcast-btn-outline broadcast-btn-add-more"
                onClick={() => setPickerOpen(true)}
                aria-label="Add more recipients"
              >
                <FaUserPlus aria-hidden="true" /> Add more
              </button>
              <div
                className={`broadcast-recipient-count ${
                  recipientOverCap ? "over-cap" : ""
                }`}
                aria-live="polite"
              >
                {recipients.length} recipient
                {recipients.length === 1 ? "" : "s"} (limit {recipientCap})
              </div>
            </div>
          )}

          {recipientOverCap && (
            <div className="broadcast-inline-error" role="alert">
              <FaExclamationTriangle aria-hidden="true" />
              <span>
                {ERROR_COPY.RECIPIENT_CAP_EXCEEDED({
                  cap: recipientCap,
                  role: isAdmin ? "admin" : "staff",
                  attempted: recipients.length,
                })}
              </span>
            </div>
          )}
        </section>

        {/* Step 2 — Subject */}
        <section
          className="broadcast-step"
          aria-labelledby="broadcast-step-subject"
        >
          <h2 id="broadcast-step-subject" className="broadcast-step-label">
            Step 2: Subject
          </h2>
          <div className="broadcast-subject-field">
            <input
              type="text"
              className="broadcast-subject-input"
              aria-label="Email subject"
              maxLength={SUBJECT_MAX_LENGTH}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject line your customers will see"
            />
            <div
              className={`broadcast-char-counter stage-${subjectCounterStage}`}
              aria-live="polite"
            >
              {subject.length}/{SUBJECT_MAX_LENGTH}
            </div>
          </div>
        </section>

        {/* Step 3 — Body */}
        <section className="broadcast-step" aria-labelledby="broadcast-step-body">
          <h2 id="broadcast-step-body" className="broadcast-step-label">
            Step 3: Message body
          </h2>

          <div
            className="broadcast-mode-tabs"
            role="tablist"
            aria-label="Compose mode"
          >
            <button
              type="button"
              role="tab"
              aria-selected={composeMode === "template"}
              tabIndex={composeMode === "template" ? 0 : -1}
              className={`broadcast-mode-chip ${
                composeMode === "template" ? "active" : ""
              }`}
              onClick={() => setComposeMode("template")}
            >
              Template
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={composeMode === "freeform"}
              tabIndex={composeMode === "freeform" ? 0 : -1}
              className={`broadcast-mode-chip ${
                composeMode === "freeform" ? "active" : ""
              }`}
              onClick={() => setComposeMode("freeform")}
            >
              Freeform
            </button>
          </div>

          {composeMode === "template" ? (
            <div
              className="broadcast-template-empty"
              role="region"
              aria-label="Template mode"
            >
              <p>
                Template-driven broadcasts will be available in an upcoming
                release. For now, use the Freeform tab to compose a one-off
                message.
              </p>
            </div>
          ) : (
            <FreeformBodyEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              charCount={bodyCharCount}
              counterStage={bodyCounterStage}
              softWarnAt={BODY_SOFT_WARN_CHARS}
              hardBlockAt={BODY_HARD_BLOCK_CHARS}
            />
          )}
        </section>

        {/* Footer actions */}
        <footer className="broadcast-footer-actions">
          <button
            type="button"
            className="broadcast-btn-text"
            onClick={handleCancel}
            disabled={sending}
          >
            Cancel
          </button>
          <div className="broadcast-footer-actions-right">
            <button
              type="button"
              className="broadcast-btn-primary"
              onClick={handleSendClick}
              disabled={sendDisabled}
              aria-disabled={sendDisabled}
              title={sendDisabled ? sendDisabledReason : "Send broadcast"}
              aria-label={sendDisabled ? sendDisabledReason : "Send broadcast"}
            >
              <FaPaperPlane aria-hidden="true" />{" "}
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        </footer>
      </div>

      {pickerOpen && (
        <RecipientPickerDrawer
          initialSelection={recipients}
          recipientCap={recipientCap}
          role={isAdmin ? "admin" : "staff"}
          onCancel={() => setPickerOpen(false)}
          onCommit={handleRecipientsCommit}
        />
      )}

      {confirmOpen && (
        <SendConfirmModal
          recipients={recipients}
          subject={subject}
          bodyHtml={bodyHtml}
          sending={sending}
          error={sendError}
          onCancel={handleConfirmCancel}
          onConfirm={handleConfirmSend}
        />
      )}
    </div>
  );
};

// Format "First L." from a display name; falls back to the raw input if a
// last name isn't extractable (one-word names, etc.).
function formatFirstInitial(displayName) {
  if (!displayName) return "";
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  return `${parts[0]} ${last.charAt(0)}.`;
}

export default BroadcastComposePage;
